import {
  container,
  DYNAMIC_ENDPOINT_MANAGER_TOKEN,
  FIREBASE_MANAGER_TOKEN,
  I18N_TOKEN,
  INTERCOM_MANAGER_TOKEN,
  MESSAGE_MANAGER_TOKEN,
  QUERY_CLIENT_TOKEN,
  QUILL_MANAGER_TOKEN,
  SERVICE_WORKER_MANAGER_TOKEN,
  SUPPORTED_LNGS_TOKEN,
  TRANSCODER_TOKEN,
  WEB_SOCKET_MANAGER_TOKEN,
} from '@/container';
import i18n, { supportedLanguages } from '@/i18n';
import { createRouter } from '@/router';
import { dynamicEndpointManager } from '@/services/dynamic-endpoint-manager';
import { createFirebaseManager } from '@/services/firebase-manager';
import { createIntercomManager } from '@/services/intercom-manager';
import { messageManager } from '@/services/message-manager';
import { createQuillManager } from '@/services/quill';
import initializeSentry from '@/services/sentry';
import { createServiceWorkerManager } from '@/services/service-worker';
import { createTranscoder } from '@/services/transcoder';
import { createWebSocketManager } from '@/services/web-socket-manager';
import { useConfigurationStore } from '@/stores/configuration';
import { APP_VERSION_HASH, BRAND_STORAGE_KEY, BUILD_ENV } from '@/utils/constants';
import { getStandaloneMode } from '@/utils/get-standalone-mode';
import { persistQueryClientOptions, queryClient, restoreQueryClientPersistence } from '@/utils/query-client';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/vi';
import '@/styles/globals.css';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type Container, injectable } from 'ditox';
import { CustomDependencyContainer } from 'ditox-react';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router/dom';
import 'unfonts.css';
import { setIsDesktop } from './stores/layout-status';

dayjs.extend(duration);
dayjs.extend(relativeTime);

function appDependencyBinder(c: Container) {
  c.bindValue(I18N_TOKEN, i18n);
  c.bindValue(SUPPORTED_LNGS_TOKEN, supportedLanguages);
  c.bindValue(QUERY_CLIENT_TOKEN, queryClient);
  c.bindValue(DYNAMIC_ENDPOINT_MANAGER_TOKEN, dynamicEndpointManager);
  c.bindValue(MESSAGE_MANAGER_TOKEN, messageManager);
  c.bindFactory(
    WEB_SOCKET_MANAGER_TOKEN,
    injectable(createWebSocketManager, DYNAMIC_ENDPOINT_MANAGER_TOKEN, I18N_TOKEN),
  );
  c.bindFactory(TRANSCODER_TOKEN, injectable(createTranscoder));
  c.bindFactory(INTERCOM_MANAGER_TOKEN, injectable(createIntercomManager));
  c.bindFactory(FIREBASE_MANAGER_TOKEN, injectable(createFirebaseManager));
  c.bindFactory(SERVICE_WORKER_MANAGER_TOKEN, injectable(createServiceWorkerManager));
  c.bindFactory(SERVICE_WORKER_MANAGER_TOKEN, injectable(createServiceWorkerManager));
  c.bindFactory(QUILL_MANAGER_TOKEN, injectable(createQuillManager), { scope: 'transient' });

  return c;
}

async function enableMSW() {
  if (process.env.NODE_ENV !== 'test') {
    return;
  }

  const { worker } = await import('../__mocks__/msw/browser');

  return worker.start();
}

async function prepareApp() {
  await enableMSW();

  if (!getStandaloneMode()) {
    const brand = useConfigurationStore.getState().envConfig.brand?.key;
    if (brand) {
      localStorage.setItem(BRAND_STORAGE_KEY, brand);
    }
  }
}

// Render the app
Promise.allSettled([restoreQueryClientPersistence(), dynamicEndpointManager.ensureInitialized, setIsDesktop()])
  .then(() => initializeSentry())
  .then(() => prepareApp())
  .then(() => {
    console.info(`[Chat Version]: ${APP_VERSION_HASH}`);
    console.info('[Build Env]', BUILD_ENV);

    let rootElement = document.getElementById('root');
    if (!rootElement) {
      rootElement = document.createElement('div');
      rootElement.dataset.ready = 'no';
      document.body.appendChild(rootElement);
    }
    if (rootElement.dataset.ready === 'no') {
      rootElement.dataset.ready = 'yes';
      const router = createRouter();
      const root = ReactDOM.createRoot(rootElement);

      root.render(
        <StrictMode>
          <CustomDependencyContainer container={appDependencyBinder(container)}>
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={persistQueryClientOptions}>
              <HelmetProvider>
                <RouterProvider router={router} />
              </HelmetProvider>
            </PersistQueryClientProvider>
          </CustomDependencyContainer>
        </StrictMode>,
      );
    }
  });
